import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

import { UrlInput } from "../components/UrlInput";
import { PreviewCard } from "../components/PreviewCard";
import { HistoryGrid } from "../components/HistoryGrid";
import { SettingsButton } from "../components/SettingsButton";
import { getSessionId } from "../lib/session";
import { getCookies } from "../lib/cookies";
import { ytDateToLocalInput } from "../lib/format";
import { randomizeMeta } from "../lib/random";
import { Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyMeta = {
  title: "",
  artist: "",
  album: "",
  comment: "",
  creation_time: "",
};

export default function Home() {
  const [sessionId] = useState(() => getSessionId());
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState(emptyMeta);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/history/${sessionId}`);
      setHistory(data || []);
    } catch (e) {
      // silent
    }
  }, [sessionId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const applyDefaults = (p) => ({
    title: p.title || "",
    artist: p.uploader ? `@${p.uploader}` : "",
    album: "Instagram",
    comment: p.description || "",
    creation_time: ytDateToLocalInput(p.upload_date),
  });

  const onFetch = async () => {
    if (!url.trim()) {
      toast.error("Вставь ссылку на Instagram");
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await axios.post(`${API}/preview`, {
        url: url.trim(),
        cookies: getCookies() || undefined,
      });
      setPreview(data);
      setMeta(applyDefaults(data));
      toast.success("Готово к скачиванию");
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Не удалось загрузить эту ссылку.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    if (preview) setMeta(applyDefaults(preview));
  };

  const onRandom = () => {
    const ref = preview ? ytDateToLocalInput(preview.upload_date) : "";
    setMeta(randomizeMeta(ref));
    toast.success("Мета-данные случайные, дата раньше загрузки");
  };

  const onDownload = async () => {
    if (!preview) return;
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/download`,
        {
          url: url.trim(),
          session_id: sessionId,
          custom_meta: meta,
          cookies: getCookies() || undefined,
        },
        { responseType: "blob", timeout: 180000 }
      );

      const disposition = response.headers["content-disposition"] || "";
      const isImg = preview?.media_type === "image";
      let filename = `${meta.title || "instagram"}.${isImg ? "jpg" : "mp4"}`;
      const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
      const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (utf8Match) {
        try { filename = decodeURIComponent(utf8Match[1]); } catch {}
      } else if (plainMatch) {
        filename = plainMatch[1];
      }

      const mediaType = response.headers["content-type"] || (isImg ? "image/jpeg" : "video/mp4");
      const blob = new Blob([response.data], { type: mediaType });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);

      toast.success("Скачано с твоими мета-данными");
      fetchHistory();
    } catch (e) {
      let msg = "Скачивание не удалось.";
      if (e?.response?.data) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } catch {}
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const onClearHistory = async () => {
    try {
      await axios.delete(`${API}/history/${sessionId}`);
      setHistory([]);
      toast.success("История очищена");
    } catch {
      toast.error("Не удалось очистить историю");
    }
  };

  const onReuse = (item) => {
    setUrl(item.url);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grain relative min-h-screen bg-[#050505] text-white overflow-hidden">
      <div className="hero-glow-a" />
      <div className="hero-glow-b" />

      <header className="relative z-20 max-w-5xl mx-auto px-6 pt-8 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl ig-gradient flex items-center justify-center shadow-[0_0_20px_rgba(220,39,67,0.35)]">
            <Download className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight">
            Reel<span className="ig-gradient-text">Grab</span>
          </span>
        </div>
        <SettingsButton />
      </header>

      <main className="relative z-10">
        <section className="pt-10 sm:pt-16 pb-8">
          <div className="max-w-3xl mx-auto px-6">
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-center mb-8"
            >
              Скачивание Instagram <span className="ig-gradient-text">Reels, Stories и фото</span>
            </motion.h1>
            <UrlInput
              url={url}
              setUrl={setUrl}
              onFetch={onFetch}
              loading={loading}
            />
          </div>
        </section>

        <section className="pt-6 pb-16 px-6">
          <AnimatePresence mode="wait">
            {preview && (
              <PreviewCard
                key="preview"
                preview={preview}
                meta={meta}
                setMeta={setMeta}
                defaults={applyDefaults(preview)}
                onReset={onReset}
                onRandom={onRandom}
                onDownload={onDownload}
                downloading={downloading}
              />
            )}
          </AnimatePresence>
        </section>

        <HistoryGrid items={history} onClear={onClearHistory} onReuse={onReuse} />
      </main>
    </div>
  );
}
